import { Link } from '@tanstack/react-router'
import { Spin, Table, Tag, Tooltip, Typography } from 'antd'
import { IndicationItem } from 'features/indicationTriage'
import React, { memo, useMemo } from 'react'
import styled from 'styled-components'

const { Text } = Typography

const EmptyStateContainer = styled.div`
  padding: 2rem;
  text-align: center;
`

interface InspectionIndicationsTableProps {
  indications: readonly IndicationItem[]
  componentId?: string
  areIndicationsLoading?: boolean
}

interface IndicationTableRow {
  key: string
  indicationId: string
  classification: string
  damageMechanism: string | number
}

const InspectionIndicationsTableComponent: React.FC<
  InspectionIndicationsTableProps
> = ({ indications, componentId, areIndicationsLoading }) => {
  const tableData: readonly IndicationTableRow[] = useMemo(() => {
    return indications.map((indication) => ({
      key: indication.id,
      indicationId: indication.id,
      classification:
        indication.classificationInfo?.classification ?? 'UNKNOWN',
      damageMechanism: indication.detail?.damage_mechanism ?? '-',
    }))
  }, [indications])

  const columns = useMemo(
    () => [
      {
        title: 'Indication ID',
        dataIndex: 'indicationId',
        key: 'indicationId',
        ellipsis: true,
        render: (id: string) => {
          if (!componentId) {
            return <Tooltip title={id}>{id}</Tooltip>
          }
          return (
            <Tooltip title={id}>
              <Link
                to='/details/$entryID'
                params={{ entryID: componentId }}
                search={{ extension: 'IndicationTriage', indication: id }}
                target='_blank'
                rel='noopener noreferrer'>
                {id}
              </Link>
            </Tooltip>
          )
        },
      },
      {
        title: 'Classification',
        dataIndex: 'classification',
        key: 'classification',
        render: (classification: string) => (
          <Tag color='blue'>{classification}</Tag>
        ),
      },
      {
        title: 'Damage Mechanism',
        dataIndex: 'damageMechanism',
        key: 'damageMechanism',
        render: (mechanism: string | number) => <Tag>{String(mechanism)}</Tag>,
      },
    ],
    [componentId]
  )

  if (areIndicationsLoading) {
    return (
      <EmptyStateContainer>
        <Spin />
      </EmptyStateContainer>
    )
  }

  if (tableData.length === 0) {
    return (
      <EmptyStateContainer>
        <Text type='secondary'>No indications found for this inspection</Text>
      </EmptyStateContainer>
    )
  }

  return (
    <Table
      dataSource={tableData}
      columns={columns}
      pagination={{ pageSize: 10, showSizeChanger: false }}
      size='small'
    />
  )
}

export const InspectionIndicationsTable = memo(
  InspectionIndicationsTableComponent
)
